/**
 * PIQ 8.0 / 8.3.2: Operator View Adapter
 * ======================================
 * Pure mapping from BrainContract to Command Center UI view models.
 * NO intelligence computation here - only data transformation.
 * 
 * PIQ 8.3.2: Added source normalization for Google/Meta items.
 */

import { normalizeSourceType, isMarketingSource } from './sourceNormalizer';

// ============================================================
// Types: BrainContract Inputs (subset we consume)
// ============================================================

export type PostureType = 'all_clear' | 'monitor_only' | 'action_required';

export interface BrainContractSubset {
  decisionPosture?: {
    posture: PostureType;
    rationale: string[];
    confidence?: number;
  };
  
  retailerAttribution?: {
    primaryDriver?: {
      retailer: string;
      contributionPct: number;
    };
    entries?: Array<{
      retailer: string;
      contributionPct: number;
    }>;
  };
  
  earlyWarnings?: {
    active: Array<{
      id: string;
      type: string;
      expectedImpactLow: number | null;
      expectedImpactHigh: number | null;
      confidence: number;
    }>;
    summary?: {
      hasActiveWarning: boolean;
    };
  };
  
  propagation?: {
    risk?: {
      probabilityLow: number;
      probabilityHigh: number;
      expectedLossLow: number;
      expectedLossHigh: number;
    } | null;
    summary?: {
      hasPropagationRisk: boolean;
    };
  };
  
  containmentPlaybook?: {
    playbook?: {
      id: string;
      title: string;
      status: string;
    } | null;
    steps?: Array<{
      id: string;
      title: string;
      description: string;
      ownerRole: string;
      roiScore: number;
      converted: boolean;
    }>;
  };
  
  preventivePlaybooks?: Array<{
    id: string;
    title: string;
    steps: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  }>;
  
  microAttribution?: Record<string, {
    topSkus?: Array<{
      skuKey: string;
      description: string;
      contribution: number;
      drivers: string[];
    }>;
  }>;
  
  retailerCausality?: {
    classification?: string;
    driverRetailer?: string;
  };
  
  valueLedger?: {
    ledgerId?: string;
    calendarWeekKey?: string;
    headline?: string;
    summaryMetrics?: {
      preventedDownsideLow: number;
      preventedDownsideHigh: number;
      realizedImpact: number;
      noiseSuppressed: number;
      noiseReductionPct: number;
    };
    summary?: {
      headline?: string;
    };
  };
  
  // Calendar week key (fallback)
  calendarWeekKey?: string;
  
  // Data quality / conflicts
  dataQuality?: {
    conflictsCount: number;
    conflictsBlocking: boolean;
    conflictDetails?: Array<{
      metricKey: string;
      message: string;
    }>;
    missingBaselines?: string[];
  };
  
  // PIQ 8.1.1: Business state for decision card
  businessState?: {
    primaryState: string;
    stateLabel: string;
    confidence: number;
  };
  
  // PIQ 8.1.1: Belief summary for decision card
  beliefSummary?: {
    primary: string | null;
    confidence: number | null;
  };
  
  // PIQ 8.1.1: CEV/unexpected value summary
  unexpectedValueSummary?: {
    totalUnexpected: number;
    status: 'material' | 'immaterial' | 'none';
  };
  
  // PIQ 8.2.2: Google Ads / Marketing advisory
  googleAdsAdvisory?: Array<{
    id: string;
    title: string;
    description: string;
    source: 'google' | 'meta' | 'klaviyo' | 'piq';
    confidence?: number;
    impactBand?: string;
  }>;
  
  // PIQ 8.2.2: Learning / drift items
  learningItems?: Array<{
    id: string;
    summary: string;
    type: 'drift' | 'regime' | 'pattern_decay';
  }>;
}

// ============================================================
// Types: UI View Models
// ============================================================

export interface OperatorStatusBar {
  posture: PostureType;
  postureLabel: string;
  postureColor: string;
  
  /** PIQ 8.1.1: One-line verdict for operator clarity */
  verdictLine: string;
  verdictType: 'act' | 'prepare' | 'monitor' | 'hold' | 'clear';
  
  primaryDriver?: {
    retailer: string;
    label: string;
    sharePct: number;
  };
  
  earlyWarning?: {
    active: boolean;
    count: number;
    impactBand: string | null;
  };
  
  propagation?: {
    active: boolean;
    probabilityBand: string;
    dollarBand: string;
  };
  
  dataHealth: {
    conflictsCount: number;
    blocking: boolean;
  };
  
  /** Value Ledger headline (PIQ 7.7) */
  valueLedger?: {
    headline: string;
    ledgerId?: string;
    weekKey?: string;
  };
  
  rationale: string[];
}

export interface ActionItem {
  id: string;
  title: string;
  description: string;
  roiScore: number;
  roiLabel: string;
  reliabilityBand: string;
  expectedImpact: string | null;
  sourceType: 'action' | 'case' | 'playbook';
  /** PIQ 8.2.3: Provenance for badge display */
  provenanceType: SourceType;
  retailer?: string;
}

export interface PrepareItem {
  id: string;
  title: string;
  description: string;
  ownerRole: string;
  steps: Array<{
    id: string;
    title: string;
    description: string;
  }>;
  canConvert: boolean;
  type: 'containment' | 'preventive';
}

export interface IgnoreItem {
  id: string;
  reason: string;
  detail: string;
}

export type InsightCategory = 'drivers' | 'patterns' | 'opportunities' | 'data_health';

/** PIQ 8.2.3: Source type for provenance badges */
export type SourceType = 'piq' | 'google' | 'meta' | 'forecast' | 'pattern' | 'manual' | 'system';

export interface InsightFeedItem {
  id: string;
  category: InsightCategory;
  categoryLabel: string;
  icon: string;
  summary: string;
  impactBand: string | null;
  confidence: number | null;
  hasEvidence: boolean;
  evidenceKey?: string;
  /** PIQ 8.2.3: Source provenance */
  sourceType: SourceType;
}

/** PIQ 8.1.1: Decision card when no actions are required */
export interface NoActionsDecision {
  title: string;
  reasons: string[];
}

/** PIQ 8.1.1: Exclusive mode - only one of ACT or PREPARE shows */
export type OperatorMode = 'act' | 'prepare' | 'hold';

export interface OperatorView {
  statusBar: OperatorStatusBar;
  
  /** PIQ 8.1.1: Exclusive mode determines which section to show */
  mode: OperatorMode;
  
  actSection: {
    visible: boolean;
    items: ActionItem[];
    maxItems: number;
    /** PIQ 8.1.1: Decision card when no actions */
    noActionsDecision: NoActionsDecision | null;
  };
  prepareSection: {
    visible: boolean;
    primaryItem: PrepareItem | null;
  };
  ignoreSection: {
    visible: boolean;
    items: IgnoreItem[];
    collapsed: boolean;
  };
  insightsFeed: {
    drivers: InsightFeedItem[];
    patterns: InsightFeedItem[];
    opportunities: InsightFeedItem[];
    marketing: InsightFeedItem[];
    dataHealth: InsightFeedItem[];
    learning: InsightFeedItem[];
  };
}

// ============================================================
// Configuration
// ============================================================

export const OPERATOR_CONFIG = {
  actSection: {
    maxItems: 3,
    minRoiThreshold: 40,
  },
  ignoreSection: {
    defaultCollapsed: true,
    maxItems: 3,
  },
  insightsFeed: {
    maxPerCategory: 5,
  },
} as const;

// ============================================================
// Helper Functions
// ============================================================

export function getPostureLabel(posture: PostureType): string {
  switch (posture) {
    case 'all_clear': return 'All Clear';
    case 'monitor_only': return 'Monitor';
    case 'action_required': return 'Action Required';
    default: return 'Unknown';
  }
}

export function getPostureColor(posture: PostureType): string {
  switch (posture) {
    case 'all_clear': return 'emerald';
    case 'monitor_only': return 'amber';
    case 'action_required': return 'red';
    default: return 'gray';
  }
}

export function getRetailerLabel(retailer: string): string {
  const labels: Record<string, string> = {
    ulta: 'Ulta Beauty',
    macys: "Macy's",
    jcp: 'JCPenney',
    dtc: 'DTC',
  };
  return labels[retailer.toLowerCase()] || retailer;
}

export function formatDollarBand(low: number, high: number): string {
  const fmt = (n: number) => {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${Math.round(n).toLocaleString()}`;
  };
  return `${fmt(low)}–${fmt(high)}`;
}

export function formatProbabilityBand(low: number, high: number): string {
  return `${Math.round(low * 100)}–${Math.round(high * 100)}%`;
}

export function getRoiLabel(score: number): string {
  if (score >= 80) return 'High ROI';
  if (score >= 60) return 'Medium ROI';
  if (score >= 40) return 'Low ROI';
  return 'Minimal ROI';
}

export function getCategoryIcon(category: InsightCategory): string {
  switch (category) {
    case 'drivers': return 'target';
    case 'patterns': return 'trending-up';
    case 'opportunities': return 'sparkles';
    case 'data_health': return 'database';
  }
}

export function getCategoryLabel(category: InsightCategory): string {
  switch (category) {
    case 'drivers': return 'Drivers';
    case 'patterns': return 'Patterns';
    case 'opportunities': return 'Opportunities';
    case 'data_health': return 'Data Health';
  }
}

// ============================================================
// Main Adapter Function
// ============================================================

export interface AdapterInput {
  brainContract: BrainContractSubset;
  unifiedActions?: Array<{
    id: string;
    title: string;
    description: string;
    priorityScore: number;
    impactDollars?: number | null;
    sourceType: 'action' | 'warning' | 'insight';
    retailerName?: string;
    confidence?: number | null;
    stateStatus?: string;
    expectation?: { status: string };
    forecastExpectation?: { status: string };
    // PIQ 8.3.3: Add type (finding_type) for proper source classification
    type?: string;
    // PIQ 8.3.3: Add metadata for Google/marketing detection
    metadata?: Record<string, unknown>;
    aiEvidence?: {
      dataSource?: string;
      inferenceType?: string;
    };
  }>;
  suppression?: {
    findings: number;
    cases: number;
    reasons: string[];
  };
}

/**
 * PIQ 8.1.1: Compute verdict line based on posture and context.
 * Deterministic rules - no intelligence computation.
 */
function computeVerdictLine(
  posture: PostureType,
  primaryDriver: string | undefined,
  hasContainment: boolean,
  hasEarlyWarning: boolean,
  hasPropagation: boolean
): { line: string; type: 'act' | 'prepare' | 'monitor' | 'hold' | 'clear' } {
  if (posture === 'action_required') {
    const driverText = primaryDriver ? `${getRetailerLabel(primaryDriver)} is driving unexpected downside` : 'material action required';
    return { line: `Act now — ${driverText}.`, type: 'act' };
  }
  
  if (posture === 'monitor_only') {
    if (hasContainment || hasPropagation) {
      return { line: 'Prepare — cascade risk detected; follow containment plan.', type: 'prepare' };
    }
    if (hasEarlyWarning) {
      return { line: 'Monitor — lead indicator detected; prep steps available.', type: 'monitor' };
    }
    return { line: 'Hold — no material actions met thresholds.', type: 'hold' };
  }
  
  return { line: 'All clear — no material issues detected.', type: 'clear' };
}

/**
 * PIQ 8.1.1: Build "No Actions Required" decision card.
 */
function buildNoActionsDecision(brainContract: BrainContractSubset): NoActionsDecision {
  const reasons: string[] = [];
  
  // Business state
  if (brainContract.businessState?.stateLabel) {
    const conf = brainContract.businessState.confidence 
      ? ` (${Math.round(brainContract.businessState.confidence * 100)}% confidence)`
      : '';
    reasons.push(`Business state: ${brainContract.businessState.stateLabel}${conf}`);
  }
  
  // Belief
  if (brainContract.beliefSummary?.primary) {
    const beliefLabel = brainContract.beliefSummary.primary.replace(/_/g, ' ');
    const conf = brainContract.beliefSummary.confidence 
      ? ` (${Math.round(brainContract.beliefSummary.confidence * 100)}% confidence)`
      : '';
    reasons.push(`Current belief: ${beliefLabel}${conf}`);
  }
  
  // CEV / unexpected value
  if (brainContract.unexpectedValueSummary) {
    if (brainContract.unexpectedValueSummary.status === 'none') {
      reasons.push('No unexpected value detected');
    } else if (brainContract.unexpectedValueSummary.status === 'immaterial') {
      reasons.push('Unexpected value below materiality threshold');
    }
  } else {
    reasons.push('No material unexpected loss detected');
  }
  
  // If no reasons, add a default
  if (reasons.length === 0) {
    reasons.push('All metrics within expected ranges');
  }
  
  return {
    title: 'No actions required this week',
    reasons,
  };
}

/**
 * Transform BrainContract into OperatorView.
 * PURE MAPPING ONLY - no intelligence computation.
 */
export function buildOperatorView(input: AdapterInput): OperatorView {
  const { brainContract, unifiedActions = [], suppression } = input;
  
  // Status Bar
  const posture = brainContract.decisionPosture?.posture || 'monitor_only';
  const primaryDriverRetailer = brainContract.retailerAttribution?.primaryDriver?.retailer;
  
  // Check for containment/early warning/propagation
  const hasContainment = !!brainContract.containmentPlaybook?.playbook;
  const hasEarlyWarning = brainContract.earlyWarnings?.summary?.hasActiveWarning ?? false;
  const hasPropagation = brainContract.propagation?.summary?.hasPropagationRisk ?? false;
  
  // PIQ 8.1.1: Compute verdict line
  const verdict = computeVerdictLine(posture, primaryDriverRetailer, hasContainment, hasEarlyWarning, hasPropagation);
  
  const statusBar: OperatorStatusBar = {
    posture,
    postureLabel: getPostureLabel(posture),
    postureColor: getPostureColor(posture),
    verdictLine: verdict.line,
    verdictType: verdict.type,
    
    primaryDriver: brainContract.retailerAttribution?.primaryDriver ? {
      retailer: brainContract.retailerAttribution.primaryDriver.retailer,
      label: getRetailerLabel(brainContract.retailerAttribution.primaryDriver.retailer),
      sharePct: brainContract.retailerAttribution.primaryDriver.contributionPct,
    } : undefined,
    
    earlyWarning: hasEarlyWarning ? {
      active: true,
      count: brainContract.earlyWarnings?.active?.length ?? 0,
      impactBand: brainContract.earlyWarnings?.active?.[0]?.expectedImpactLow != null &&
                  brainContract.earlyWarnings?.active?.[0]?.expectedImpactHigh != null
        ? formatDollarBand(
            brainContract.earlyWarnings!.active[0].expectedImpactLow!,
            brainContract.earlyWarnings!.active[0].expectedImpactHigh!
          )
        : null,
    } : undefined,
    
    propagation: hasPropagation && brainContract.propagation?.risk ? {
      active: true,
      probabilityBand: formatProbabilityBand(
        brainContract.propagation.risk.probabilityLow,
        brainContract.propagation.risk.probabilityHigh
      ),
      dollarBand: formatDollarBand(
        brainContract.propagation.risk.expectedLossLow,
        brainContract.propagation.risk.expectedLossHigh
      ),
    } : undefined,
    
    dataHealth: {
      conflictsCount: brainContract.dataQuality?.conflictsCount ?? 0,
      blocking: brainContract.dataQuality?.conflictsBlocking ?? false,
    },
    
    // Value Ledger headline (PIQ 7.7) - pure mapping, no computation
    valueLedger: (() => {
      const headline = brainContract.valueLedger?.headline ||
                       brainContract.valueLedger?.summary?.headline ||
                       undefined;
      if (!headline) return undefined;
      return {
        headline,
        ledgerId: brainContract.valueLedger?.ledgerId,
        weekKey: brainContract.valueLedger?.calendarWeekKey || brainContract.calendarWeekKey,
      };
    })(),
    
    rationale: brainContract.decisionPosture?.rationale ?? [],
  };
  
  // ACT Section
  const actionableItems = unifiedActions
    .filter(a => a.stateStatus !== 'state_suppressed')
    .filter(a => a.priorityScore >= OPERATOR_CONFIG.actSection.minRoiThreshold)
    .slice(0, OPERATOR_CONFIG.actSection.maxItems);
  
  // PIQ 8.1.1: Build no-actions decision card
  const noActionsDecision = actionableItems.length === 0 
    ? buildNoActionsDecision(brainContract)
    : null;
  
  // PIQ 8.1.1: Mode exclusivity
  // If action_required OR actions exist → ACT mode
  // Else if containment exists → PREPARE mode
  // Else if preventive exists → PREPARE mode
  // Else → HOLD mode
  let mode: OperatorMode = 'hold';
  if (posture === 'action_required' || actionableItems.length > 0) {
    mode = 'act';
  } else if (hasContainment) {
    mode = 'prepare';
  } else if (brainContract.preventivePlaybooks?.length) {
    mode = 'prepare';
  }
  
  const actSection = {
    // PIQ 8.1.1: Only visible in ACT mode
    visible: mode === 'act',
    items: actionableItems.map(a => ({
      id: a.id,
      title: a.title,
      description: a.description,
      roiScore: a.priorityScore,
      roiLabel: getRoiLabel(a.priorityScore),
      reliabilityBand: 'medium', // From BrainContract in real impl
      expectedImpact: a.impactDollars ? formatDollarBand(a.impactDollars * 0.8, a.impactDollars * 1.2) : null,
      sourceType: a.sourceType === 'warning' ? 'case' : a.sourceType as 'action' | 'case',
      // PIQ 8.2.3: Provenance for source badge
      provenanceType: (a.forecastExpectation ? 'forecast' : 
                       a.sourceType === 'insight' ? 'pattern' : 'piq') as SourceType,
      retailer: a.retailerName,
    })),
    maxItems: OPERATOR_CONFIG.actSection.maxItems,
    noActionsDecision,
  };
  
  // PREPARE Section
  let primaryPrepare: PrepareItem | null = null;
  
  // Containment takes priority over preventive
  if (brainContract.containmentPlaybook?.playbook) {
    const pb = brainContract.containmentPlaybook;
    primaryPrepare = {
      id: pb.playbook!.id,
      title: pb.playbook!.title,
      description: 'Containment plan for detected propagation risk',
      ownerRole: 'Ops',
      steps: (pb.steps || []).map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
      })),
      canConvert: pb.playbook!.status === 'active',
      type: 'containment',
    };
  } else if (brainContract.preventivePlaybooks?.length) {
    const pp = brainContract.preventivePlaybooks[0];
    primaryPrepare = {
      id: pp.id,
      title: pp.title,
      description: 'Preparatory steps based on early warning',
      ownerRole: 'Founder',
      steps: pp.steps.map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
      })),
      canConvert: false,
      type: 'preventive',
    };
  }
  
  const prepareSection = {
    // PIQ 8.1.1: Only visible in PREPARE mode
    visible: mode === 'prepare',
    primaryItem: primaryPrepare,
  };
  
  // IGNORE Section
  const ignoreReasons: IgnoreItem[] = [];
  
  // Add suppression reasons
  if (suppression?.reasons?.length) {
    suppression.reasons.slice(0, OPERATOR_CONFIG.ignoreSection.maxItems).forEach((reason, idx) => {
      ignoreReasons.push({
        id: `suppress-${idx}`,
        reason: 'Expected Movement',
        detail: reason,
      });
    });
  }
  
  // Add expected items from unified actions
  unifiedActions
    .filter(a => a.expectation?.status === 'within_expected' || a.forecastExpectation?.status === 'within_expected_error_band')
    .slice(0, OPERATOR_CONFIG.ignoreSection.maxItems - ignoreReasons.length)
    .forEach(a => {
      ignoreReasons.push({
        id: a.id,
        reason: 'Within Expected Range',
        detail: a.title,
      });
    });
  
  const ignoreSection = {
    visible: ignoreReasons.length > 0,
    items: ignoreReasons,
    collapsed: OPERATOR_CONFIG.ignoreSection.defaultCollapsed,
  };
  
  // Insights Feed
  const insightsFeed: OperatorView['insightsFeed'] = {
    drivers: [],
    patterns: [],
    opportunities: [],
    marketing: [],
    dataHealth: [],
    learning: [],
  };
  
  // Drivers: Attribution + Causality + Micro-attribution
  if (brainContract.retailerAttribution?.entries?.length) {
    brainContract.retailerAttribution.entries.slice(0, 3).forEach((entry, idx) => {
      insightsFeed.drivers.push({
        id: `driver-${idx}`,
        category: 'drivers',
        categoryLabel: getCategoryLabel('drivers'),
        icon: getCategoryIcon('drivers'),
        summary: `${getRetailerLabel(entry.retailer)}: ${Math.round(entry.contributionPct * 100)}% of change`,
        impactBand: null,
        confidence: null,
        hasEvidence: true,
        evidenceKey: 'retailer_attribution',
        sourceType: 'piq',
      });
    });
  }
  
  if (brainContract.retailerCausality?.driverRetailer) {
    insightsFeed.drivers.push({
      id: 'causality-driver',
      category: 'drivers',
      categoryLabel: getCategoryLabel('drivers'),
      icon: getCategoryIcon('drivers'),
      summary: `${getRetailerLabel(brainContract.retailerCausality.driverRetailer)} is leading indicator`,
      impactBand: null,
      confidence: null,
      hasEvidence: true,
      evidenceKey: 'causality',
      sourceType: 'piq',
    });
  }
  
  // Micro-attribution SKUs
  if (brainContract.microAttribution) {
    Object.entries(brainContract.microAttribution).forEach(([retailer, data]) => {
      if (data.topSkus?.length) {
        data.topSkus.slice(0, 2).forEach((sku, idx) => {
          insightsFeed.drivers.push({
            id: `micro-${retailer}-${idx}`,
            category: 'drivers',
            categoryLabel: getCategoryLabel('drivers'),
            icon: getCategoryIcon('drivers'),
            summary: `${sku.description}: ${Math.round(sku.contribution * 100)}% contribution`,
            impactBand: null,
            confidence: null,
            hasEvidence: true,
            evidenceKey: `micro_${retailer}`,
            sourceType: 'piq',
          });
        });
      }
    });
  }
  
  // PIQ 8.3.3: Process insights - route to correct group based on normalized sourceType
  // INVARIANT: Google/Meta -> Marketing, System/data_quality -> Data Health, only true patterns -> Patterns
  unifiedActions
    .filter(a => a.sourceType === 'insight')
    .slice(0, OPERATOR_CONFIG.insightsFeed.maxPerCategory)
    .forEach(a => {
      // PIQ 8.3.3: Normalize sourceType with full context (type, metadata, title)
      const normalizedSource = normalizeSourceType({
        ...a,
        type: (a as any).type,  // finding_type from backend
        metadata: (a as any).metadata,
        aiEvidence: (a as any).aiEvidence,
      });
      
      // PIQ 8.3.3: Route to marketing if Google/Meta
      if (isMarketingSource(normalizedSource)) {
        insightsFeed.marketing.push({
          id: a.id,
          category: 'opportunities',
          categoryLabel: normalizedSource === 'google' ? 'Google Recommendation' : 'Meta Recommendation',
          icon: getCategoryIcon('opportunities'),
          summary: a.title,
          impactBand: a.impactDollars ? formatDollarBand(a.impactDollars * 0.8, a.impactDollars * 1.2) : null,
          confidence: a.confidence ?? null,
          hasEvidence: true,
          sourceType: normalizedSource,
        });
        return; // Skip
      }
      
      // PIQ 8.3.3: Route to data health if system/data quality
      if (normalizedSource === 'system') {
        insightsFeed.dataHealth.push({
          id: a.id,
          category: 'data_health',
          categoryLabel: 'Data Quality',
          icon: getCategoryIcon('data_health'),
          summary: a.title,
          impactBand: null,
          confidence: null,
          hasEvidence: false,
          sourceType: 'system',
        });
        return; // Skip
      }
      
      // Only true patterns go to patterns group
      insightsFeed.patterns.push({
        id: a.id,
        category: 'patterns',
        categoryLabel: getCategoryLabel('patterns'),
        icon: getCategoryIcon('patterns'),
        summary: a.title,
        impactBand: a.impactDollars ? formatDollarBand(a.impactDollars * 0.8, a.impactDollars * 1.2) : null,
        confidence: a.confidence ?? null,
        hasEvidence: true,
        sourceType: 'pattern',
      });
    });
  
  // PIQ 8.3.3: Opportunities - positive impact items
  // Route Google/Meta to marketing, system to data health
  unifiedActions
    .filter(a => (a.impactDollars ?? 0) > 0)
    .slice(0, OPERATOR_CONFIG.insightsFeed.maxPerCategory)
    .forEach(a => {
      // PIQ 8.3.3: Normalize sourceType with full context
      const normalizedSource = normalizeSourceType({
        ...a,
        type: (a as any).type,
        metadata: (a as any).metadata,
        aiEvidence: (a as any).aiEvidence,
      });
      
      if (isMarketingSource(normalizedSource)) {
        // Check if already added to marketing
        if (!insightsFeed.marketing.some(m => m.id === `opp-${a.id}` || m.id === a.id)) {
          insightsFeed.marketing.push({
            id: `opp-${a.id}`,
            category: 'opportunities',
            categoryLabel: normalizedSource === 'google' ? 'Google Recommendation' : 'Meta Recommendation',
            icon: getCategoryIcon('opportunities'),
            summary: a.title,
            impactBand: a.impactDollars ? formatDollarBand(a.impactDollars * 0.8, a.impactDollars * 1.2) : null,
            confidence: a.confidence ?? null,
            hasEvidence: true,
            sourceType: normalizedSource,
          });
        }
        return; // Skip
      }
      
      if (normalizedSource === 'system') {
        // Route to data health instead
        if (!insightsFeed.dataHealth.some(m => m.id === a.id)) {
          insightsFeed.dataHealth.push({
            id: `opp-${a.id}`,
            category: 'data_health',
            categoryLabel: 'Data Quality',
            icon: getCategoryIcon('data_health'),
            summary: a.title,
            impactBand: null,
            confidence: null,
            hasEvidence: false,
            sourceType: 'system',
          });
        }
        return; // Skip
      }
      
      insightsFeed.opportunities.push({
        id: `opp-${a.id}`,
        category: 'opportunities',
        categoryLabel: getCategoryLabel('opportunities'),
        icon: getCategoryIcon('opportunities'),
        summary: a.title,
        impactBand: a.impactDollars ? formatDollarBand(a.impactDollars * 0.8, a.impactDollars * 1.2) : null,
        confidence: a.confidence ?? null,
        hasEvidence: true,
        sourceType: a.forecastExpectation ? 'forecast' : 'piq',
      });
    });
  
  // Data Health: conflicts, missing baselines
  if (brainContract.dataQuality?.conflictsBlocking) {
    insightsFeed.dataHealth.push({
      id: 'conflict-blocking',
      category: 'data_health',
      categoryLabel: getCategoryLabel('data_health'),
      icon: getCategoryIcon('data_health'),
      summary: `Data integrity issue: ${brainContract.dataQuality.conflictsCount} conflicts blocking key metrics`,
      impactBand: null,
      confidence: null,
      hasEvidence: false,
      sourceType: 'system',
    });
  } else if (brainContract.dataQuality?.conflictsCount) {
    insightsFeed.dataHealth.push({
      id: 'conflict-info',
      category: 'data_health',
      categoryLabel: getCategoryLabel('data_health'),
      icon: getCategoryIcon('data_health'),
      summary: `${brainContract.dataQuality.conflictsCount} non-blocking data conflicts`,
      impactBand: null,
      confidence: null,
      hasEvidence: false,
      sourceType: 'system',
    });
  }
  
  if (brainContract.dataQuality?.missingBaselines?.length) {
    insightsFeed.dataHealth.push({
      id: 'missing-baselines',
      category: 'data_health',
      categoryLabel: getCategoryLabel('data_health'),
      icon: getCategoryIcon('data_health'),
      summary: `Missing baselines: ${brainContract.dataQuality.missingBaselines.join(', ')}`,
      impactBand: null,
      confidence: null,
      hasEvidence: false,
      sourceType: 'system',
    });
  }
  
  // PIQ 8.2.2 / 8.2.4: Marketing / Google Ads advisory
  // PIQ 8.2.4: Google items MUST appear in Marketing group with correct badge
  if (brainContract.googleAdsAdvisory?.length) {
    brainContract.googleAdsAdvisory.forEach((item, idx) => {
      const sourceLabel = item.source === 'google' ? 'Google' : 
                          item.source === 'meta' ? 'Meta' :
                          item.source === 'klaviyo' ? 'Klaviyo' : 'PIQ';
      // PIQ 8.2.3/8.2.4: Map source to sourceType - Google items NEVER get 'pattern' or 'piq'
      const sourceType: SourceType = item.source === 'google' ? 'google' :
                                     item.source === 'meta' ? 'meta' : 'piq';
      // PIQ 8.2.4: Prefix title with source for clarity
      const titlePrefix = item.source === 'google' ? 'Google Ads: ' :
                          item.source === 'meta' ? 'Meta Ads: ' : '';
      insightsFeed.marketing.push({
        id: `marketing-${idx}`,
        // PIQ 8.2.4: Category must be 'opportunities' but goes into marketing array
        category: 'opportunities',
        categoryLabel: `${sourceLabel} Recommendation`,
        icon: getCategoryIcon('opportunities'),
        summary: `${titlePrefix}${item.title}`,
        impactBand: item.impactBand || null,
        confidence: item.confidence || null,
        hasEvidence: true,
        evidenceKey: `marketing_${item.source}_${idx}`,
        sourceType,
      });
    });
  }
  
  // PIQ 8.2.2: Learning items (drift, regime, pattern decay)
  if (brainContract.learningItems?.length) {
    brainContract.learningItems.forEach((item, idx) => {
      const typeLabel = item.type === 'drift' ? 'Drift Detected' :
                        item.type === 'regime' ? 'Regime Change' : 'Pattern Decay';
      insightsFeed.learning.push({
        id: `learning-${idx}`,
        category: 'data_health',
        categoryLabel: typeLabel,
        icon: getCategoryIcon('data_health'),
        summary: item.summary,
        impactBand: null,
        confidence: null,
        hasEvidence: false,
        sourceType: 'piq',
      });
    });
  }
  
  // PIQ 8.3.3: INVARIANT CHECKS - Marketing items MUST be in marketing group
  if (process.env.NODE_ENV === 'development') {
    // Check 1: No Google/Meta sourceType in patterns
    const googleInPatterns = insightsFeed.patterns.filter(
      p => p.sourceType === 'google' || p.sourceType === 'meta'
    );
    if (googleInPatterns.length > 0) {
      console.error('[operatorViewAdapter] INVARIANT VIOLATED: Google/Meta items found in patterns array', googleInPatterns);
    }
    
    // Check 2: No items with "campaign budget" in title in patterns
    const campaignBudgetInPatterns = insightsFeed.patterns.filter(
      p => p.summary.toLowerCase().includes('campaign budget')
    );
    if (campaignBudgetInPatterns.length > 0) {
      console.error('[operatorViewAdapter] INVARIANT VIOLATED: "campaign budget" item found in patterns (should be marketing)', campaignBudgetInPatterns);
    }
    
    // Check 3: If googleAdsAdvisory has items, marketing must have items
    const googleCount = brainContract.googleAdsAdvisory?.length || 0;
    if (googleCount > 0 && insightsFeed.marketing.length === 0) {
      console.error('[operatorViewAdapter] INVARIANT VIOLATED: Google advisory count > 0 but marketing array is empty');
    }
    
    // Check 4: No data_quality items in patterns (should be in dataHealth)
    const dataQualityInPatterns = insightsFeed.patterns.filter(
      p => p.summary.toLowerCase().includes('data discrepanc') || 
           p.summary.toLowerCase().includes('resolve data')
    );
    if (dataQualityInPatterns.length > 0) {
      console.error('[operatorViewAdapter] INVARIANT VIOLATED: Data quality items found in patterns (should be data_health)', dataQualityInPatterns);
    }
    
    // Summary log
    console.log('[operatorViewAdapter] Feed group counts:', {
      patterns: insightsFeed.patterns.length,
      marketing: insightsFeed.marketing.length,
      dataHealth: insightsFeed.dataHealth.length,
      opportunities: insightsFeed.opportunities.length,
    });
  }
  
  return {
    statusBar,
    mode,
    actSection,
    prepareSection,
    ignoreSection,
    insightsFeed,
  };
}

/**
 * Check if view should show ACT section.
 */
export function shouldShowActSection(view: OperatorView): boolean {
  return view.actSection.visible && view.actSection.items.length > 0;
}

/**
 * Check if view should show PREPARE section.
 */
export function shouldShowPrepareSection(view: OperatorView): boolean {
  return view.prepareSection.visible && view.prepareSection.primaryItem !== null;
}

/**
 * Get total insights count.
 */
export function getInsightsCount(view: OperatorView): number {
  return (
    view.insightsFeed.drivers.length +
    view.insightsFeed.patterns.length +
    view.insightsFeed.opportunities.length +
    view.insightsFeed.marketing.length +
    view.insightsFeed.dataHealth.length +
    view.insightsFeed.learning.length
  );
}

// ============================================================
// PIQ 8.2.1: Insight Detail VM Mapping
// ============================================================

export interface InsightDetailVM {
  id: string;
  title: string;
  category: InsightCategory | 'marketing' | 'conflict';
  categoryLabel: string;
  confidence: number | null;
  impactBand: string | null;
  why: string;
  evidenceRefs: Array<{
    label: string;
    type: 'query' | 'metric' | 'external';
    ref: string;
  }>;
  context: {
    phase?: string;
    belief?: string;
    state?: string;
    regime?: string;
  };
  relatedCaseId?: string;
  relatedPlaybookId?: string;
  conflictData?: {
    metricKey: string;
    values: Array<{ source: string; value: string | number }>;
    lineage?: string;
  };
  driverData?: {
    retailer?: string;
    contributionPct?: number;
    direction?: string;
    skus?: Array<{ key: string; description: string; contribution: number }>;
  };
}

/**
 * PIQ 8.2.5: Compute feed group from sourceType
 * INVARIANT: google/meta items can NEVER appear in Patterns group
 * This is the authoritative grouping function.
 */
export type FeedGroup = 'patterns' | 'opportunities' | 'marketing' | 'data_health' | 'learning' | 'drivers';

export function computeFeedGroup(item: InsightFeedItem): FeedGroup {
  // PIQ 8.2.5: Google/Meta items ALWAYS go to marketing, regardless of category
  if (item.sourceType === 'google' || item.sourceType === 'meta') {
    return 'marketing';
  }
  
  // System items go to data_health
  if (item.sourceType === 'system') {
    return 'data_health';
  }
  
  // Pattern items go to patterns
  if (item.sourceType === 'pattern') {
    return 'patterns';
  }
  
  // Forecast items go to opportunities
  if (item.sourceType === 'forecast') {
    return 'opportunities';
  }
  
  // Default: use category as guide
  if (item.category === 'drivers') {
    return 'drivers';
  }
  if (item.category === 'data_health') {
    return 'data_health';
  }
  if (item.category === 'patterns') {
    return 'patterns';
  }
  if (item.category === 'opportunities') {
    return 'opportunities';
  }
  
  // Default fallback
  return 'patterns';
}

/**
 * PIQ 8.2.5: Validate that google items are not in patterns
 * Returns true if invariant holds, false if violated
 */
export function validateGoogleNotInPatterns(patterns: InsightFeedItem[]): boolean {
  return !patterns.some(item => item.sourceType === 'google' || item.sourceType === 'meta');
}

/**
 * PIQ 8.2.1 / 8.2.3: Map InsightFeedItem to InsightDetailVM for drawer display.
 * Pure mapping - no intelligence computation.
 */
export function mapFeedItemToDetailVM(item: InsightFeedItem): InsightDetailVM {
  return {
    id: item.id,
    title: item.summary,
    category: item.category,
    categoryLabel: item.categoryLabel,
    confidence: item.confidence,
    impactBand: item.impactBand,
    why: `This insight surfaced because it met the threshold criteria for ${item.categoryLabel.toLowerCase()}.`,
    evidenceRefs: item.hasEvidence && item.evidenceKey
      ? [{ label: 'View evidence', type: 'query' as const, ref: item.evidenceKey }]
      : [],
    context: {},
    // PIQ 8.2.3: Pass through source type for badge display
    sourceType: item.sourceType,
  };
}
